<section class="submit-container">
  <!-- Step: reporting-periods -->
  <app-reporting-periods-list
    *ngIf="currentStep === 'reporting-periods'"
    [$periods]="periods"
    [$profileIncomplete]="profileIncomplete"
    (selectPeriod)="onPeriodSelected($event)"
  ></app-reporting-periods-list>

  <!-- Step: carry-forward -->
  <app-carry-forward-preview
    *ngIf="currentStep === 'carry-forward'"
    [questionnaire]="questionnaire"
    [previousSubmission]="previousSubmission"
    [loading]="loading"
    (confirm)="onCarryForwardConfirm()"
    (edit)="onCarryForwardEdit()"
    (startFresh)="onCarryForwardStartFresh()"
  ></app-carry-forward-preview>

  <!-- Step: disclaimer -->
  <div *ngIf="currentStep === 'disclaimer'" class="disclaimer-container">
    <div class="card p-5">
      <div class="content">
        <h2 class="title is-4">Disclaimer</h2>

        <ul>
          <li>
            Participation in this Reporting Framework is voluntary and does not
            constitute compliance with, or equivalence to, other frameworks,
            whose scope and expectations may vary.
          </li>
          <li>
            Responses will be published in full on the OECD.AI website and
            attributed to the submitting organisation. The OECD does not review,
            screen, verify, or redact the content of submissions prior to
            publication.
          </li>
          <li>
            Submitting organisations are solely responsible for the content of
            their submissions, including its accuracy and the appropriateness of
            any information disclosed. Organisations must not include
            confidential, proprietary, personal, or otherwise sensitive
            information. By submitting a report, organisations confirm that all
            necessary internal authorisations have been obtained and accept
            responsibility for any information disclosed, whether intentionally
            or inadvertently.
          </li>
          <li>
            Recognition under the HAIP Brand does not:
            <ul>
              <li>
                Constitute an endorsement of an organisation's practices or the
                AI systems it develops or uses.
              </li>
              <li>
                Certify compliance with the HAIP International Code of Conduct
                for Organisations Developing Advanced AI Systems.
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>

    <div class="card p-5 mt-4" *ngIf="!submission?.haveReadDisclaimer">
      <div class="mb-5">
        <label class="checkbox">
          <input
            type="checkbox"
            class="checkmark"
            [(ngModel)]="disclaimerChecked"
          />
          By submitting this survey, you confirm that the information provided is accurate
          and truthful at the time of the submission to the best of your knowledge.
        </label>
      </div>
      <button
        type="button"
        [disabled]="!disclaimerChecked || loading"
        [class.is-loading]="loading"
        (click)="acceptDisclaimer()"
        class="button is-link is-medium is-fullwidth"
      >
        Accept and continue
      </button>
    </div>
    <div class="mt-4" *ngIf="submission?.haveReadDisclaimer">
      <a
        (click)="navigateTo('edit', { reportingPeriod: reportingPeriodValue })"
        class="button is-white"
      >
        <i class="icon icon-arrow-left mr-1"></i> Back to questionnaire
      </a>
    </div>
  </div>

  <!-- Step: edit (accordion questionnaire) -->
  <div *ngIf="currentStep === 'edit'" class="accordion-questionnaire mb-huge">
    <div class="autosave-banner mb-4">
      <i class="icon icon-zap"></i>
      <span
        >Your responses are automatically saved and you can return to them at
        any time.</span
      >
    </div>

    <div class="is-flex is-justify-content-space-between mb-5">
      <a
        (click)="
          navigateTo('disclaimer', { reportingPeriod: reportingPeriodValue })
        "
        class="disclaimer-link"
      >
        <i class="icon icon-info mr-1"></i> View disclaimer
      </a>
      <div class="is-flex" style="gap: 1rem">
        <a
          *ngIf="submission && (isAdmin || isNotProduction)"
          (click)="fillWithRandomData()"
          class="random-fill-link"
        >
          <i class="icon icon-zap mr-1"></i> Fill in with random data
        </a>
        <a
          *ngIf="submission"
          (click)="discardDraft()"
          class="discard-link"
        >
          <i class="icon icon-trash-2 mr-1"></i> Discard draft
        </a>
      </div>
    </div>

    <div
      *ngFor="let section of questionnaire; let i = index"
      class="accordion-panel"
      [ngClass]="{ 'is-expanded': expandedSection === section.sectionNumber }"
    >
      <div
        class="accordion-header"
        (click)="toggleSection(section.sectionNumber)"
      >
        <div class="section-indicator">
          <i
            *ngIf="getSectionProgress(section.sectionNumber) === 100"
            class="icon icon-check-circle section-complete"
          ></i>
          <svg
            *ngIf="
              getSectionProgress(section.sectionNumber) > 0 &&
              getSectionProgress(section.sectionNumber) < 100
            "
            class="section-progress-ring"
            viewBox="0 0 24 24"
          >
            <circle
              class="progress-bg"
              cx="12"
              cy="12"
              r="10"
              fill="none"
              stroke-width="2.5"
            />
            <circle
              class="progress-fill"
              cx="12"
              cy="12"
              r="10"
              fill="none"
              stroke-width="2.5"
              [attr.stroke-dasharray]="2 * 3.14159 * 10"
              [attr.stroke-dashoffset]="
                2 *
                3.14159 *
                10 *
                (1 - getSectionProgress(section.sectionNumber) / 100)
              "
              transform="rotate(-90 12 12)"
            />
          </svg>
          <span
            *ngIf="getSectionProgress(section.sectionNumber) === 0"
            class="section-circle"
          ></span>
        </div>
        <div class="section-info">
          <span class="section-number"
            >{{ section.sectionNumber === 0 ? 'Preliminary' : 'Section ' + section.sectionNumber }}</span
          >
          <span class="section-title">{{ section.title }}</span>
        </div>
        <div class="section-chevron">
          <i
            class="icon"
            [ngClass]="{
              'icon-chevron-up': expandedSection === section.sectionNumber,
              'icon-chevron-down': expandedSection !== section.sectionNumber
            }"
          ></i>
        </div>
      </div>

      <div
        class="accordion-body"
        *ngIf="expandedSection === section.sectionNumber && form"
      >
        <p class="section-description mb-5" *ngIf="section.description">
          {{ section.description }}
        </p>

        <form [formGroup]="form">
          <ng-container *ngFor="let field of resolvedFields">
            <ng-container *caseHasPermission="field.permission">
              <div class="question-card" [id]="field.id" *ngIf="!field.hidden" [style.margin-left.rem]="(field['indentLevel'] || 0) * 2">
                <div class="recommended-actors" *ngIf="field['recommendedActors']?.length">
                  <span
                    class="tag"
                    *ngFor="let actor of field['recommendedActors']"
                    [appHtmlTooltip]="getActorDescription(actor)"
                  >
                    {{ actor }}
                  </span>
                </div>
                <ng-container *ngIf="field['isHeading']; else formInput">
                  <label class="label">{{ field.label }}</label>
                  <div
                    class="columns mt-1"
                    *ngIf="shouldShowRefs(field)"
                  >
                    <div class="column">
                      <app-ref-search-input
                        label="Tools used..."
                        icon="icon-grid"
                        [resources]="toolResources"
                        [initialValue]="
                          getToolRefInitialValue(getQuestionId(field))
                        "
                        [maxResults]="5"
                        [readonly]="isReadOnly"
                        resourceType="tool"
                        (valueChanged)="
                          onRefChanged(getQuestionId(field), 'tool', $event)
                        "
                      ></app-ref-search-input>
                    </div>
                    <div class="column">
                      <app-ref-search-input
                        label="Metrics used..."
                        icon="icon-bar-chart"
                        [resources]="metricResources"
                        [initialValue]="
                          getMetricRefInitialValue(getQuestionId(field))
                        "
                        [maxResults]="5"
                        [readonly]="isReadOnly"
                        resourceType="metric"
                        (valueChanged)="
                          onRefChanged(getQuestionId(field), 'metric', $event)
                        "
                      ></app-ref-search-input>
                    </div>
                  </div>
                </ng-container>

                <ng-template #formInput>
                <ng-container *ngIf="field['isMatrix']; else defaultInput">
                  <app-matrix-input
                    [config]="field['matrixConfig']"
                    [initialValue]="field['matrixValue'] || {}"
                    [initialSpecifyText]="field['matrixSpecifyText'] || {}"
                    [label]="field.label"
                    [required]="field.required"
                    [scopeId]="field.property"
                    (valueChanged)="onMatrixValueChanged($event, field)"
                  ></app-matrix-input>
                </ng-container>

                <ng-template #defaultInput>
                  <case-input
                    [id]="field.id"
                    [type]="field.inputType"
                    [label]="field.label"
                    [placeholder]="field.placeholder"
                    [secondPlaceholder]="field.secondPlaceholder"
                    [initialValue]="field.initialValue"
                    [searchResources]="field.searchResources"
                    [filterResource]="field.filter"
                    [groupBy]="field.groupBy"
                    [resourceName]="definition.slug"
                    [searchParams]="field.searchParams"
                    [maxSelectedItems]="field.maxSelectedItems"
                    [selectAllDisabled]="field.selectAllDisabled"
                    [selectOptions]="field.selectOptions"
                    [askFileYear]="field.askFileYear"
                    [renderLabelAsHtml]="field.renderLabelAsHtml"
                    [editorMinHeight]="field.editorMinHeight"
                    [min]="field.min"
                    [max]="field.max"
                    [showCreateButton]="field.showCreateButton"
                    [copyDateFromOnDateTo]="field.copyDateFromOnDateTo"
                    [readonly]="field.readonly"
                    [validators]="field.validators"
                    [required]="field.required"
                    [helpText]="field.helpText"
                    [htmlHelpText]="field.htmlHelpText"
                    [htmlTooltip]="field.htmlTooltip"
                    [htmlExample]="field.htmlExample"
                    [showErrors]="showErrors"
                    (valueChanged)="onValueChanged($event, field)"
                  ></case-input>
                </ng-template>

                <ng-container
                  *ngIf="field['isCheckbox'] && field['checkboxItems']"
                >
                  <ng-container *ngFor="let item of field['checkboxItems']">
                    <div
                      *ngIf="
                        item.hasSpecify &&
                        isCheckboxItemSelected(field, item.value)
                      "
                      class="specify-input mt-2 ml-4"
                    >
                      <label class="label is-small"
                        >Please specify for "{{ item.label }}" <span class="has-text-danger">*</span>:</label
                      >
                      <input
                        type="text"
                        class="input"
                        [class.is-danger]="isSpecifyTextMissing(field, item.value)"
                        [value]="field['specifyText']?.[item.value] || ''"
                        (input)="onSpecifyTextChange(field, item.value, $event)"
                        placeholder="Please specify..."
                      />
                      <p class="help is-danger" *ngIf="isSpecifyTextMissing(field, item.value)">This field is required</p>
                    </div>
                  </ng-container>
                </ng-container>

                <div
                  class="columns mt-1"
                  *ngIf="shouldShowRefs(field)"
                >
                  <div class="column">
                    <app-ref-search-input
                      label="Tools used..."
                      icon="icon-grid"
                      [resources]="toolResources"
                      [initialValue]="
                        getToolRefInitialValue(getQuestionId(field))
                      "
                      [maxResults]="5"
                      [readonly]="isReadOnly"
                      resourceType="tool"
                      (valueChanged)="
                        onRefChanged(getQuestionId(field), 'tool', $event)
                      "
                    ></app-ref-search-input>
                  </div>
                  <div class="column">
                    <app-ref-search-input
                      label="Metrics used..."
                      icon="icon-bar-chart"
                      [resources]="metricResources"
                      [initialValue]="
                        getMetricRefInitialValue(getQuestionId(field))
                      "
                      [maxResults]="5"
                      [readonly]="isReadOnly"
                      resourceType="metric"
                      (valueChanged)="
                        onRefChanged(getQuestionId(field), 'metric', $event)
                      "
                    ></app-ref-search-input>
                  </div>
                </div>
                </ng-template>
                <div class="question-footnotes" *ngIf="field['footnotes']?.length">
                  <div *ngFor="let fn of field['footnotes']" class="footnote-item">
                    <sup>{{ fn.marker }}</sup> <span [innerHTML]="fn.text"></span>
                  </div>
                </div>
              </div>
            </ng-container>
          </ng-container>
        </form>

        <div class="is-flex is-justify-content-flex-end is-align-items-center mt-5 mb-4">
          <span
            *ngIf="isLastSection && completionPercentage !== 100"
            class="has-text-grey mr-3"
          >
            Complete all required fields to review ({{ completionPercentage }}%)
          </span>
          <button
            (click)="continueToNextSection()"
            class="button is-link"
            [ngClass]="{ 'is-loading': loading }"
            [disabled]="isLastSection && completionPercentage !== 100"
          >
            Continue <span class="ml-1">&rarr;</span>
          </button>
        </div>
      </div>
    </div>

    <div class="review-section mt-5">
      <button
        (click)="
          saveAndNavigateTo('review', { reportingPeriod: reportingPeriodValue })
        "
        class="button is-link is-medium is-fullwidth"
        [ngClass]="{ 'is-loading': loading }"
        [disabled]="completionPercentage !== 100"
      >
        Review your answers
      </button>
      <div
        class="review-status mt-3"
        *ngIf="submission && completionPercentage !== 100"
      >
        <span
          >You have not completed all required fields marked with
          <span class="has-text-danger">*</span></span
        >
        <span class="completion-pct">{{ completionPercentage }}% complete</span>
      </div>
    </div>
  </div>

  <!-- Step: review -->
  <div class="mb-huge" *ngIf="currentStep === 'review'">
    <app-questionnaire-review
      [questionnaire]="questionnaire"
      [submission]="submission"
      [readOnly]="isReadOnly"
      [periodLabel]="currentPeriodLabel"
      (editSection)="goToEditSection($event)"
    ></app-questionnaire-review>

    <div *ngIf="submission?.status === 'pending_review'" class="mt-4">
      <a
        (click)="unsubmitReport()"
        class="button is-warning"
        [ngClass]="{ 'is-loading': loading }"
      >
        Unsubmit and edit
      </a>
    </div>

    <div
      *ngIf="!isReadOnly"
      class="mt-5 p-4"
      style="border: 1px solid #e8e8e8; border-radius: 4px"
    >
      <label class="checkbox confirm-checkbox">
        <input type="checkbox" [(ngModel)]="confirmSubmit" />
        By submitting this survey, you confirm that the information provided is
        accurate and truthful at the time of submission to the best of your
        knowledge.
      </label>
    </div>

    <div
      class="is-flex is-align-items-flex-end is-justify-content-space-between mt-4 mb-4"
      *ngIf="!isReadOnly"
    >
      <a
        (click)="
          navigateTo('edit', {
            reportingPeriod: reportingPeriodValue,
            section: questionnaire.length
          })
        "
        class="button mr-auto is-white"
        [ngClass]="{ 'is-loading': loading }"
      >
        Previous
      </a>

      <button
        (click)="submitReport()"
        class="button is-link"
        [ngClass]="{ 'is-loading': loading }"
        [disabled]="!confirmSubmit || loading"
      >
        Submit
      </button>
    </div>
  </div>

  <!-- Discard draft confirmation modal -->
  <div class="modal" [ngClass]="{ 'is-active': showDiscardConfirm }">
    <div class="modal-background" (click)="cancelDiscard()"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Discard draft</p>
        <button class="delete" aria-label="close" (click)="cancelDiscard()"></button>
      </header>
      <section class="modal-card-body">
        <p>Are you sure you want to discard this draft? This action cannot be undone.</p>
      </section>
      <footer class="modal-card-foot">
        <button
          class="button is-danger"
          [ngClass]="{ 'is-loading': loading }"
          (click)="confirmDiscard()"
        >
          Discard
        </button>
        <button class="button" (click)="cancelDiscard()">Cancel</button>
      </footer>
    </div>
  </div>
</section>
